import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/common/PasswordInput";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { LOGIN } from "@/constants/testIds/auth";
import { IrisBrand } from "@/components/common/IrisBrand";
import { API } from "@/lib/api";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated } = useApp();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // A backend endpoint like the DMS file download route redirects here with a full
  // backend URL (e.g. .../api/dms/files/xyz/download) to bounce back to after login —
  // that's an external navigation target, not an in-app route, so it uses a real page
  // navigation (window.location) rather than React Router's navigate(). Only ever
  // honored if it actually points at our own backend, to avoid an open-redirect
  // (someone crafting a login link with ?redirect=https://evil.example).
  const redirectRaw = new URLSearchParams(location.search).get("redirect");
  const redirectParam = redirectRaw && redirectRaw.startsWith(API) ? redirectRaw : null;
  const goToDestination = () => {
    if (redirectParam) window.location.href = redirectParam;
    else navigate(location.state?.from || "/", { replace: true });
  };

  useEffect(() => {
    if (isAuthenticated) goToDestination();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password) { setError("Email and password are required"); return; }
    setSubmitting(true);
    try {
      await login(email, password);
      goToDestination();
    } catch (err) {
      setError(err?.response?.data?.detail || "Invalid email or password");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex justify-center">
          <IrisBrand size="lg" />
        </div>

        <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
          <h1 className="font-display text-lg font-semibold text-foreground">Sign in</h1>
          <p className="mt-1 text-sm text-muted-foreground">Welcome back. Enter your credentials to continue.</p>

          <form className="mt-6 space-y-4" onSubmit={submit}>
            <div>
              <Label htmlFor="login-email">Email</Label>
              <Input id="login-email" type="email" autoComplete="username" className="mt-1"
                data-testid={LOGIN.emailInput} value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="login-password">Password</Label>
              <PasswordInput id="login-password" autoComplete="current-password" className="mt-1"
                data-testid={LOGIN.passwordInput} value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>

            {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

            <Button type="submit" className="w-full" data-testid={LOGIN.submitButton} disabled={submitting}>
              {submitting ? "Signing in..." : "Sign in"}
            </Button>

            <div className="text-center">
              <Link to="/forgot-password" data-testid={LOGIN.forgotPasswordLink} className="text-sm text-primary hover:underline">
                Forgot password?
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
